import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { Link } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { Card, CardHeader, CardContent } from '../components/ui/card';

function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const addToast = useToast();

  useEffect(() => {
    const fetchProject = async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, description, project_groups!inner (id, name)')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching project:', error);
        addToast('Error fetching project: ' + error.message, 'error');
      } else {
        setProject(data);
      }
    };

    const fetchTopLevelTasks = async () => {
      const { data, error } = await supabase
        .from('tasks')
        .select('id, name, description, parent_task_id')
        .eq('project_id', id);

      if (error) {
        console.error('Error fetching top-level tasks:', error);
        addToast('Error fetching top-level tasks: ' + error.message, 'error');
      } else {
        setTasks(data);
      }
      setLoading(false);
    };

    fetchProject();
    fetchTopLevelTasks();
  }, [id, addToast]);

  const fetchChildTasks = async (parentId) => {
    const { data, error } = await supabase
      .from('tasks')
      .select('id, name, description, parent_task_id')
      .eq('parent_task_id', parentId);

    if (error) {
      console.error('Error fetching child tasks:', error);
      addToast('Error fetching child tasks: ' + error.message, 'error');
      return [];
    } else {
      return data;
    }
  };

  const renderTasks = (tasks) => {
    return tasks.map(task => (
      <li key={task.id} className="mb-2">
        <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-md">
          <strong className="text-gray-900 dark:text-gray-100">{task.name}</strong>: 
          <span className="text-gray-600 dark:text-gray-400 ml-2">{task.description}</span>
        </div>
        {task.childTasks && task.childTasks.length > 0 && (
          <ul className="ml-4 mt-2 border-l-2 border-gray-200 dark:border-gray-700 pl-4">
            {renderTasks(task.childTasks)}
          </ul>
        )}
      </li>
    ));
  };

  const loadChildTasks = async (task) => {
    const childTasks = await fetchChildTasks(task.id);
    task.childTasks = childTasks;
    for (const childTask of childTasks) {
      await loadChildTasks(childTask);
    }
    setTasks([...tasks]);
  };

  useEffect(() => {
    tasks.forEach(task => {
      loadChildTasks(task);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {loading ? (
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      ) : (
        project && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{project.name}</h1>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 dark:text-gray-400 mb-4">{project.description}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Project Group: {' '}
                  <Link 
                    to={`/project-group/${project.project_groups.id}`}
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    {project.project_groups.name}
                  </Link>
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Tasks</h2>
              </CardHeader>
              <CardContent>
                {tasks.length > 0 ? (
                  <ul className="space-y-3">
                    {renderTasks(tasks)}
                  </ul>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    No tasks found for this project.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )
      )}
    </div>
  );
}

export default ProjectDetail;